import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { BRANDING } from "@/config/branding";
import Layout from "@/components/Layout";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Transactions from "@/pages/Transactions";
import Savings from "@/pages/Savings";
import FinancialInbox from "@/pages/FinancialInbox";
import Insights from "@/pages/Insights";
import Investments from "@/pages/Investments";
import Invoices from "@/pages/Invoices";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";
import Support from "@/pages/Support";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Status from "@/pages/Status";
import Admin from "@/pages/Admin";
import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import Properties from "@/pages/Properties";
import PropertyWorkspace from "@/pages/PropertyWorkspace";
import { ModuleRoute } from "@/components/ModuleRoute";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="font-display text-3xl tracking-[0.25em] gold-text animate-pulse">{BRANDING.name}</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (!user.onboarding_complete && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

// App shell: guests landing on "/" see the public Landing page; everywhere else requires auth.
function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) {
    if (location.pathname === "/") return <Landing />;
    return <Navigate to="/auth" replace />;
  }
  if (!user.onboarding_complete && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return <Layout />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role === "admin") return children;
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route element={<AppShell />}>
              <Route path="/" element={<Home />} />
              <Route path="/cash-flow" element={<ModuleRoute module="expenses"><Transactions /></ModuleRoute>} />
              <Route path="/savings" element={<ModuleRoute module="savings"><Savings /></ModuleRoute>} />
              <Route path="/inbox" element={<ModuleRoute module="documents"><FinancialInbox /></ModuleRoute>} />
              <Route path="/insights" element={<ModuleRoute module="ai_coach"><Insights /></ModuleRoute>} />
              <Route path="/portfolio" element={<ModuleRoute module="portfolio"><Investments /></ModuleRoute>} />
              <Route path="/suppliers" element={<ModuleRoute module="supplier_invoices"><Invoices /></ModuleRoute>} />
              <Route path="/properties" element={<ModuleRoute module="properties"><Properties /></ModuleRoute>} />
              <Route path="/properties/:id" element={<ModuleRoute module="properties"><PropertyWorkspace /></ModuleRoute>} />
              <Route path="/insurance" element={<ModuleRoute module="insurance"><ComingSoon title="Insurance" /></ModuleRoute>} />
              <Route path="/bookings" element={<ModuleRoute module="bookings"><ComingSoon title="Bookings" /></ModuleRoute>} />
              <Route path="/treasurer" element={<ModuleRoute module="treasurer"><ComingSoon title="Treasurer" /></ModuleRoute>} />
              <Route path="/members" element={<ModuleRoute module="member_management"><ComingSoon title="Members" /></ModuleRoute>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/support" element={<Support />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/status" element={<Status />} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            </Route>
            <Route path="/platform-admin" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="light" />
      </AuthProvider>
    </div>
  );
}

export default App;
